import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/Components/ui/button';
import { Label } from '@/Components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/Components/ui/card';
import { CheckCircle2, AlertCircle, Lock, Eye, EyeClosed, KeyRound } from 'lucide-react';

const ResetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [tenantId, setTenantId] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    useEffect(() => {
        // Extract email, token, and tenantId from URL
        const emailParam = searchParams.get('email');
        const tokenParam = searchParams.get('token');
        const tenantIdParam = searchParams.get('tenantId');
        
        if (!emailParam || !tokenParam) {
            setError('Invalid password reset link. Please request a new one.');
            return;
        }
        
        setEmail(emailParam);
        setToken(tokenParam);
        setTenantId(tenantIdParam);
    }, [searchParams]);

    const validatePassword = (password: string): string[] => {
        const errors: string[] = [];
        
        if (password.length < 8) {
            errors.push('Password must be at least 8 characters long');
        }
        if (!/[A-Z]/.test(password)) {
            errors.push('Password must contain at least one uppercase letter');
        }
        if (!/[a-z]/.test(password)) {
            errors.push('Password must contain at least one lowercase letter');
        }
        if (!/[0-9]/.test(password)) {
            errors.push('Password must contain at least one number');
        }
        if (!/[^A-Za-z0-9]/.test(password)) {
            errors.push('Password must contain at least one special character');
        }
        
        return errors;
    };

    const handlePasswordChange = (password: string) => {
        setNewPassword(password);
        setPasswordErrors(validatePassword(password));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        // Validate password requirements
        const errors = validatePassword(newPassword);
        if (errors.length > 0) {
            setError(errors.join('. '));
            return;
        }
        
        setIsLoading(true);
        
        try {
            const response = await fetch('/Users/reset-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    token,
                    newPassword,
                    tenantId, // Include tenantId for multi-tenant support
                }),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                setError(data.error || 'Failed to reset password. The link may have expired.');
                return;
            }
            
            setSuccess(true);
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
            
        } catch (err) {
            console.error(err);
            setError('An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!email || !token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-destructive" />
                            Invalid Link
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
                            <p className="text-sm">
                                This password reset link is invalid. Please request a new password reset.
                            </p>
                        </div>
                        <Button 
                            onClick={() => navigate('/login')} 
                            className="w-full mt-4"
                        >
                            Return to Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            Password Reset Successful
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-lg border border-green-500/50 bg-green-500/10 p-4 text-green-600 dark:text-green-400">
                            <p className="text-sm">
                                Your password has been reset successfully. You will be redirected to the login page in a few seconds.
                            </p>
                        </div>
                        <Button 
                            onClick={() => navigate('/login')} 
                            className="w-full mt-4"
                        >
                            Go to Login Now
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5" />
                        Reset Password
                    </CardTitle>
                    <CardDescription>
                        Enter your new password for {email}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                                <div className="flex items-center gap-2 text-destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <p className="text-sm">{error}</p>
                                </div>
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="newPassword">New Password</Label>
                            <div className="flex flex-row w-full bg-gray-200 rounded">
                                <label className="text-gray-500 font-bold content-center ml-1" htmlFor="newPassword"><KeyRound /></label>
                                <input
                                    id="newPassword"
                                    type={showPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => handlePasswordChange(e.target.value)}
                                    placeholder="Enter new password"
                                    className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                    required
                                    disabled={isLoading}
                                />
                                <div className="content-center bg-gray-200 text-gray-500 p-2 rounded" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <Eye /> : <EyeClosed />}
                                </div>
                            </div>
                            {passwordErrors.length > 0 && newPassword.length > 0 && (
                                <div className="text-xs text-muted-foreground space-y-1">
                                    {passwordErrors.map((err, idx) => (
                                        <div key={idx} className="flex items-center gap-1">
                                            <AlertCircle className="h-3 w-3" />
                                            {err}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm Password</Label>
                            <div className="flex flex-row w-full bg-gray-200 rounded">
                                <label className="text-gray-500 font-bold content-center ml-1" htmlFor="confirmPassword"><KeyRound /></label>
                                <input
                                    id="confirmPassword"
                                    type={showPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                    required
                                    disabled={isLoading}
                                />
                                <div className="content-center bg-gray-200 text-gray-500 p-2 rounded" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <Eye /> : <EyeClosed />}
                                </div>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <div className="text-xs text-muted-foreground">
                                Password requirements:
                                <ul className="list-disc list-inside mt-1 space-y-1">
                                    <li>At least 8 characters long</li>
                                    <li>One uppercase letter</li>
                                    <li>One lowercase letter</li>
                                    <li>One number</li>
                                    <li>One special character</li>
                                </ul>
                            </div>
                        </div>
                        
                        <button 
                            type="submit" 
                            className="w-full button-default" 
                            disabled={isLoading || passwordErrors.length > 0}
                        >
                            {isLoading ? 'Resetting Password...' : 'Reset Password'}
                        </button>
                        
                        <button 
                            type="button"
                            className="w-full flex items-center justify-center gap-2 shadow bg-gray-600 hover:bg-gray-400 focus:shadow-outline focus:outline-none text-white font-bold py-2 px-4 rounded" 
                            onClick={() => navigate('/login')}
                            disabled={isLoading}
                        >
                            Back to Login
                        </button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default ResetPassword;
